/**
 * "Open prompt group" on a texts-not-filled error (user, 20/10/2026: "a
 * button to update the creative for this kind of error… without moving the
 * page… show directly which one was the problem"): the exact prompt rule
 * the fill used — a text rule, else a sender rule — opens in the Creative
 * AI group editor right here; saved, the next Fill texts uses it.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { FileText } from 'lucide-react';

import { GroupFormModal } from '@/features/campaign-texts/components/groups/GroupFormModal/GroupFormModal';
import { useSenderPromptGroups, useUpdateSenderGroup } from '@/features/campaign-texts/hooks/useSenderPromptGroups';
import { useTextPromptGroups, useUpdateGroup } from '@/features/campaign-texts/hooks/useTextPromptGroups';
import type { CreateGroupPayload } from '@/features/campaign-texts/types/campaign-texts.types';
import { getApiErrorMessage } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';

import { SectionAction } from '../../timeline/section/SectionAction';

export const OpenPromptGroup: FC<{ groupId: string }> = ({ groupId }) => {
  const [open, setOpen] = useState(false);
  const texts = useTextPromptGroups();
  const senders = useSenderPromptGroups();
  const updateText = useUpdateGroup();
  const updateSender = useUpdateSenderGroup();
  const { success, error } = useToast();
  const text = texts.data?.find((g) => g.id === groupId) ?? null;
  const sender = text ? null : (senders.data?.find((g) => g.id === groupId) ?? null);
  const group = text ?? sender;
  if (!group) return null; // not loaded yet, or the rule was deleted since
  const save = async (data: CreateGroupPayload) => {
    try {
      await (text ? updateText : updateSender).mutateAsync({ id: groupId, ...data });
      success(`“${group.name}” saved — press Fill texts again to use it`);
      setOpen(false);
    } catch (err) {
      error('Could not save the prompt group', getApiErrorMessage(err));
    }
  };
  return (
    <>
      <SectionAction icon={FileText} onClick={() => setOpen(true)}>
        Open prompt group “{group.name}”
      </SectionAction>
      <GroupFormModal
        open={open}
        mode={text ? 'text' : 'sender'}
        allTextGroups={texts.data ?? []}
        allSenderGroups={senders.data ?? []}
        group={group}
        isPending={updateText.isPending || updateSender.isPending}
        onClose={() => setOpen(false)}
        onSubmit={(data) => void save(data)}
      />
    </>
  );
};
